
import { defineComponent, onMounted, watch, PropType, computed } from 'vue'
import Bwip  from 'bwip-js'
import prntr from 'prntr'
import { kebabCase } from 'lodash'
import { downloadCanvas } from '@/utils/file'
import { BarcodeTypeId } from '@/types/barcode'

export default defineComponent({
  name: 'Barcode',
  props: {
    modelValue: {
      type: [String, Number],
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    largeTitle: {
      type: Boolean,
      default: false
    },
    id: {
      type: String,
      default: Date.now().toString(16)
    },
    format: {
      type: String as PropType<BarcodeTypeId | 'ean'>,
      required: true,
      default: () => BarcodeTypeId.code128
    },
    barWidth: {
      type: Number,
      default: 3
    },
    barScale: {
      type: Number,
      default: 2
    },
    barHeight: {
      type: Number,
      default: 40
    },
    overlay: {
      type: Boolean,
      default: true
    },
    displayValue: {
      type: Boolean
    },
    options: {
      type: Object as PropType<Bwip.ToBufferOptions>
    }
  },
  emits: ['download', 'print'],
  setup (props, { emit }) {
    onMounted(() => {
      render()
    })

    watch(() => props.modelValue, render)
    watch(() => props.barWidth, render)
    watch(() => props.barHeight, render)
    watch(() => props.barScale, render)

    const barcodeInstanceId = computed(() => `barcode-${kebabCase(props.id)}-${kebabCase(String(props.modelValue))}`)

    function getBarcodeFormatType (key, value):BarcodeTypeId {
      switch (key) {
        case 'ean':
          switch (value?.length) {
            case 2:
              return BarcodeTypeId.ean2
            case 5:
              return BarcodeTypeId.ean5
            case 8:
              return BarcodeTypeId.ean8
            case 13:
              return BarcodeTypeId.ean13
            default:
              return BarcodeTypeId.ean13
          }
        default:
          return key ? BarcodeTypeId[key] : BarcodeTypeId.qrcode
      }
    }

    function render () {
      if (!props.modelValue) return
      try {
        Bwip.toCanvas(`#${barcodeInstanceId.value}`, {
            bcid:        getBarcodeFormatType(props.format, props.modelValue),
            text:        String(props.modelValue),
            scale:       props.barScale * 4,
            width:       props.barWidth,
            height:      props.barHeight,
            includetext: props.displayValue,
            textxalign:  'center',
            ...(props.options || {})
        })
      } catch (e) {
        console.warn(`Could not render barcode of type ${props.format} with value ${props.modelValue}`,e)
      }
    }

    function onDownloadClick () {
      const dataUrl = (document.getElementById(`${barcodeInstanceId.value}`) as HTMLCanvasElement)?.toDataURL('image/png')
      downloadCanvas(dataUrl)
      emit('download', {element: barcodeInstanceId.value, dataUrl})
    }

    function onPrintClick () {
      const dataUrl = (document.getElementById(`${barcodeInstanceId.value}`) as HTMLCanvasElement)?.toDataURL('image/png')
      prntr({
        frameId: Date.now().toString(16),
        printable: `<p id="name">${props.title?.substring(0,255)}</p><img src="${dataUrl}" /><p id="sku">${props.modelValue}</p>`,
        type: 'raw-html',
        style: `img { margin: 0.15cm; display: block; margin: 0 auto; max-height: 1.8cm; text-align: center; } #name, #sku { text-align: center; font-family: Courier; font-size: 24px; width: 100%; max-height: 1.3cm; margin-top: 0.3cm; margin-bottom: 0.15cm; display: block; color: black; } #name { ${props.largeTitle ? 'font-size: 32px;' : 'font-size: 18px;'} font-weight: 700; margin-bottom: 0.6cm }`
      })
      emit('print', barcodeInstanceId.value)
    }

    return {
      onDownloadClick,
      onPrintClick,
      barcodeInstanceId
    }
  }
})
